import { useEffect, useMemo, useState } from 'react'
import { FormikProvider, useFormik, useFormikContext } from 'formik'
import { filter, mapValues, sortBy } from 'lodash'
import moment from 'moment'
import { faUpload } from '@fortawesome/pro-regular-svg-icons'
// TODO: Move to shared utils
import {
  selectOnboardingEstimates,
  selectUserTaxEstimateById,
} from '@/features/taxes/quarterly/userTaxEstimates.selector'
import {
  fetchUserTaxEstimates,
  updateUserTaxEstimates,
  UserTaxEstimate,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'

import { useReselector } from '@/utils/sharedHooks'
import { getUserDocumentById } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import {
  Button,
  Card,
  Icon,
  Text,
  CardColors,
  Label,
  Dialog,
  Form,
  Separator,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { PostOnboardingTaskIdents } from './config'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { centsToDollars, dollarsToCents } from '@/utils/currencyHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'
import { UpdateQuarterlyTaxEstimateDialog } from '@/features/taxes/quarterly/components/UpdateQuarterlyTaxEstimateDialog'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'

// TODO: Migrate to V2
import { FormikHiddenInput } from '@/componentsV1/BaseComponents'
import {
  makeDateSchema,
  makeNumberSchema,
} from '@/componentsV1/BaseComponents/FormInputs'

const QTEInput = ({
  estimate: { id, filingState, userDocumentId },
}: {
  estimate: UserTaxEstimate
}) => {
  const [fileUploadOpen, setFileUploadOpen] = useState(false)
  const singleEstimate = useReselector(selectUserTaxEstimateById, id)

  // If the form is closed without saving most of it does not lose state but with the way the documents are
  // loaded/reloaded the past form state gets lost.  This fetches the document id value on load so it will restore
  // properly
  const { values } = useFormikContext<{ estimates: UserTaxEstimate[] }>()

  const document = useReselector(
    getUserDocumentById,
    values.estimates[id]?.userDocumentId || userDocumentId
  )

  const [uploadedFile, setUploadedFile] = useState({
    title: document?.fileDescription || '',
    url: document?.signedUrl || '',
    id: document?.id,
  })

  useEffect(() => {
    if (document?.id) {
      setUploadedFile({
        title: document.fileDescription || '',
        url: document.signedUrl || '',
        id: document.id,
      })
    }
  }, [document?.fileDescription, document?.id, document?.signedUrl])

  return (
    <>
      <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
        {filingState ? `State - ${filingState}` : 'Federal'}
      </Text>
      <div className="flex flex-col md:flex-row gap-4 mt-2">
        <div className="md:w-2/3 w-full">
          <Form.Input
            label="How much did you pay?"
            placeholder="Enter amount here"
            required
            name={`estimates.${id}.amountPaid`}
            schema={makeNumberSchema({
              field: 'amount paid',
              allowedDecimals: 2,
            })}
            componentType="currency"
          />
        </div>
        <div className="md:w-1/3 w-full">
          <Form.DateInput
            label="When did you pay?"
            required
            fullWidth
            name={`estimates.${id}.paidAt`}
            schema={makeDateSchema({ field: 'amount paid' })}
            maxDate={new Date()}
          />
        </div>
      </div>

      <div className="mt-6">
        <Label title="Attach receipt" />
        <Button
          onClick={() => setFileUploadOpen(true)}
          size="md"
          variant="secondary"
          className="mt-2"
        >
          <Icon icon={faUpload} className="mr-2" />
          Upload Receipt
        </Button>

        {uploadedFile?.title && (
          <div className="mt-2">
            <Text variant={TypographyStyles.BODY_SM} className="font-bold">
              {uploadedFile.title}
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className="italic">
              Can be found in your &quot;Tax&quot; Documents
            </Text>
          </div>
        )}

        <FormikHiddenInput
          name={`estimates.${id}.userDocumentId`}
          value={uploadedFile.id}
        />
      </div>
      <UpdateQuarterlyTaxEstimateDialog
        open={fileUploadOpen}
        close={() => setFileUploadOpen(false)}
        estimate={singleEstimate as UserTaxEstimate}
      />
    </>
  )
}

const QuarterPanel = ({
  estimates,
  title,
}: {
  estimates: UserTaxEstimate[]
  title: string
}) => {
  if (!estimates.length) {
    return null
  }

  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="space-y-4">
        <Text variant={TypographyStyles.H2}>{title}</Text>
        {sortBy(estimates, 'type', 'filingState').map((estimate) => (
          <QTEInput estimate={estimate} key={`estimate-row-${estimate.id}`} />
        ))}
      </div>
    </Card>
  )
}

export const OnboardingEstimatedTaxPaymentsModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const estimates = useReselector(selectOnboardingEstimates)
  const year = useReselector(selectCurrentAnnualTaxYear)
  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserTaxEstimates())
  }, [dispatch])

  // Transform the estimates into values digestible by the form
  const initialValues = useMemo(
    () => ({
      estimates: mapValues(
        estimates,
        ({ id, paidAt, amountPaidInCents, userDocumentId }) => ({
          id,
          paidAt: paidAt ? moment(paidAt).format(DATE_FORMATS.INPUT) : '',
          amountPaid: amountPaidInCents
            ? centsToDollars(amountPaidInCents)
            : '',
          userDocumentId,
        })
      ),
    }),
    [estimates]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: async ({ estimates }) => {
      await Promise.all(
        Object.values(estimates).map(
          ({ id, paidAt, amountPaid, userDocumentId }) =>
            dispatch(
              updateUserTaxEstimates(id, {
                paidAt: moment(paidAt, DATE_FORMATS.INPUT).valueOf(),
                amountPaidInCents: amountPaid
                  ? dollarsToCents(amountPaid)
                  : null,
                userDocumentId,
              })
            )
        )
      )

      await markUserActionItemCompleteIfExists(
        PostOnboardingTaskIdents.ENTER_QTE_PAYMENTS,
        (event, properties) => track(event, properties)
      )
      onClose()
    },
  })

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <Dialog.Content className="max-w-2xl">
        <FormikProvider value={formik}>
          <Dialog.Title>Enter estimated tax payments for {year}</Dialog.Title>

          <div className="space-y-4 py-2">
            <Text variant={TypographyStyles.BODY_LG}>
              Enter your estimated tax payments for this year. Include any
              receipts if you have them. You can view estimated payments you’ve
              made on the federal or state tax portals.
            </Text>

            <Separator />

            <QuarterPanel
              title="Q1 (January - March)"
              estimates={filter(
                estimates,
                (estimate) => estimate.taxQuarter === `${year}-1`
              )}
            />

            <QuarterPanel
              title="Q2 (April - July)"
              estimates={filter(
                estimates,
                (estimate) => estimate.taxQuarter === `${year}-2`
              )}
            />

            <QuarterPanel
              title="Q3 (August - September)"
              estimates={filter(
                estimates,
                (estimate) => estimate.taxQuarter === `${year}-3`
              )}
            />
          </div>

          <Dialog.Footer className="mt-6 flex justify-end gap-3">
            <Dialog.Close asChild>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={formik.submitForm}>Submit</Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
